import { ControlledTextInput } from '@components/ControlledTextInput';
import { Collapsible, Typography } from '@gurupay/gp-ui-library';
import { useBicValidation } from '@pages/payments/hooks/useBicValidation.ts';
import { PaymentAddressInput, PaymentDetailsSwitcher } from '@pages/templates/components';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CreatePaymentTemplate } from 'types/templates';

export const InternationalPayment: FC = () => {
	const { t } = useTranslation();
	const {
		control,
		formState: { errors },
	} = useFormContext<CreatePaymentTemplate>();

	const benBic = useBicValidation(
		'beneficiary.bank',
		undefined,
		t('payments.common.bicPatternValidation'),
	);
	const corrBic = useBicValidation(
		'correspondent',
		undefined,
		t('payments.newPayment.additionalData.swift.additionalData.bicPatternValidation'),
	);

	return (
		<>
			<PaymentAddressInput<CreatePaymentTemplate>
				prefix="beneficiary.address"
				translatePrefix="payments.newPayment.additionalData.address"
			/>
			<div className="space-y-space-16 max-w-125">
				<Typography level="h2">{t('payments.newPayment.data.title')}</Typography>
				<Typography level="h4">
					{t('payments.newPayment.data.transferType.label')}
				</Typography>
				<Controller
					control={control}
					name="payment_details"
					render={({ field }) => (
						<PaymentDetailsSwitcher<CreatePaymentTemplate>
							{...field}
							error={errors?.payment_details}
						/>
					)}
					rules={{
						maxLength: {
							message: t('payments.common.maxLength', { maxLength: 140 }),
							value: 140,
						},
					}}
				/>
				<Typography level="h4">
					{t('payments.newPayment.additionalData.beneficiaryBank')}
				</Typography>
				<div>
					<ControlledTextInput
						isLoading={benBic.isValidating}
						label={t('payments.newPayment.additionalData.swift.bic')}
						name="beneficiary.bank.bic"
						placeholder="GUPULT22XXX"
						rules={{
							validate: benBic.validate,
						}}
					/>
				</div>

				<ControlledTextInput
					disabled={benBic.isLocked}
					label={t('payments.newPayment.additionalData.swift.additionalData.name')}
					name="beneficiary.bank.name"
				/>
				<PaymentAddressInput<CreatePaymentTemplate>
					prefix="beneficiary.bank.address"
					translatePrefix="payments.newPayment.additionalData.address"
					disabled={benBic.isLocked}
					unstructuredLineLabelKey="bankLine"
				/>
			</div>
			<Collapsible title={t('payments.newPayment.additionalData.correspondentBank')}>
				<div className="space-y-space-16 my-space-16 max-w-125">
					<ControlledTextInput
						isLoading={corrBic.isValidating}
						label={t('payments.newPayment.additionalData.swift.additionalData.bic')}
						name="correspondent.bic"
						placeholder={'GUPULT22XXX'}
						rules={{
							validate: corrBic.validate,
						}}
					/>

					<ControlledTextInput
						disabled={corrBic.isLocked}
						label={t('payments.newPayment.additionalData.swift.additionalData.name')}
						name="correspondent.name"
					/>
					<PaymentAddressInput<CreatePaymentTemplate>
						prefix="correspondent.address"
						translatePrefix="payments.newPayment.additionalData.address"
						disabled={corrBic.isLocked}
						unstructuredLineLabelKey="bankLine"
					/>
				</div>
			</Collapsible>
		</>
	);
};
